import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { useCallback } from 'react';
import { FlaskConical } from 'lucide-react';

import { notifySuccess } from '@/portainer/services/notifications';
import { ExperimentalFeatures } from '@/react/portainer/settings/types';
import { useUpdateExperimentalSettingsMutation } from '@/react/portainer/settings/queries';

import { LoadingButton } from '@@/buttons/LoadingButton';
import { TextTip } from '@@/Tip/TextTip';

import { EnableObservabilitySwitch } from './EnableObservabilitySwitch';

interface FormValues {
  Observability: boolean;
}
const validation = yup.object({
  OpenAIIntegration: yup.boolean(),
  Observability: yup.boolean(),
});

interface Props {
  settings: ExperimentalFeatures;
}

export function ExperimentalFeaturesSettingsForm({ settings }: Props) {
  const initialValues: FormValues = settings;
  const updateSettingsMutation = useUpdateExperimentalSettingsMutation();

  const handleSubmit = useCallback(
    ({ Observability }: FormValues) => {
      updateSettingsMutation.mutate(
        {
          Observability,
        },
        {
          onSuccess() {
            notifySuccess(
              'Success',
              'Successfully updated experimental features settings'
            );
          },
        }
      );
    },
    [updateSettingsMutation]
  );

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validation}
      validateOnMount
      enableReinitialize
    >
      {({ isValid, dirty }) => (
        <Form className="form-horizontal">
          <TextTip color="blue" icon={FlaskConical}>
            Experimental features may be discontinued without notice.
          </TextTip>

          <br />
          <br />

          <div className="form-group col-sm-12 text-muted small">
            In Portainer releases, we may introduce features that we&apos;re
            experimenting with. These will be items in the early phases of
            development with limited testing.
            <br />
            Our goal is to gain early user feedback, so we can refine, enhance
            and ultimately make our features the best they can be. Disabling an
            experimental feature will prevent access to it.
          </div>

          <div className="form-group col-sm-12">
            {/* Observability Feature */}
            <div className="flex">
              <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 th-dark:text-white mb-3">
                  Observability
                </h4>
                <EnableObservabilitySwitch />
              </div>
            </div>
          </div>

          <div className="form-group mt-8">
            <div className="col-sm-12">
              <LoadingButton
                loadingText="Saving settings..."
                isLoading={updateSettingsMutation.isLoading}
                disabled={!isValid || !dirty}
                className="!ml-0"
                data-cy="settings-experimentalButton"
              >
                Save experimental settings
              </LoadingButton>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
}
